import { CurrencyEnum } from '../../enums';
import lang from '../../language';
import { DynamicFilterType } from '../../services/filter/types';
import { NullableNumber, NullableString, TypedObject } from '../../util/types';
import { BasePageableRequest, OptionalPageAndSizeRequest, OptionalSortingRequest } from '../pageable';

export enum SymbologyType {
  NASDAQ = 'NASDAQ',
  CQS = 'CQS',
  CMS = 'CMS',
  JAPANESE = 'JAPANESE',
  FOREX = 'FOREX',
  ENX = 'ENX',
  SECTOR = 'SECTOR',
  GERMAN = 'GERMAN',
}

export type BaseSymbolData = {
  symbol: string;
  logoBase64?: string;
  companyName?: string;
  url?: string;
};

export type BaseSymbolDataWithName = {
  symbol: string;
  name?: string;
  url?: string;
};

export type SymbolAndStockExchangeCode = {
  symbol: string;
  stockExchangeCode: StockExchangeCodeType;
};

export interface AvailableIndexesResponse {
  symbol: string;
  asYahoo: string;
  name: string;
  currency: CurrencyEnum;
  stockExchange: string;
  exchangeShortName: string;
}

export interface PopularStock extends BaseSymbolData {
  popularity: number;
  symbolUniverseSymbol: boolean;
}

export interface PopularStockParams {
  constrained?: boolean;
  count?: number;
}

export interface DividendPayerData extends BaseSymbolDataWithName {
  currentDividendPayment: number;
  dividendYield: number;
  marketCap: number;
  order: number;
  previousDividendPayment: number;
  volume: number;
  close: number;
  dividendRate: number;
}

export type PageableFilterRequest = BasePageableRequest & {
  isSymbolUniverseSymbols?: boolean;
};

export interface HighGrowthCompanyData extends BaseSymbolDataWithName {
  avg30Volume: number;
  epsLast12Months: number;
  growthIndicator: number;
  marketCap: number;
  order: number;
  revenue: number;
}

export interface TopGainerAndLoserData extends BaseSymbolDataWithName {
  changePercentage?: string;
  price?: number;
  absoluteChange?: string;
}

export interface EpicMoversResponse {
  topGainers: TopGainerAndLoserData[];
  topLosers: TopGainerAndLoserData[];
}

export const TopGainersAndLosersPriceOption = {
  oneToFive: lang.commonXtoY('$1', '$5'),
  fiveToFifteen: lang.commonXtoY('$5', '$15'),
  fifteenToFifty: lang.commonXtoY('$15', '$50'),
  fiftyToHundred: lang.commonXtoY('$50', '$100'),
  overHundred: lang.commonOverX('$100'),
};

/**
 * @deprecated please use EpicMoversQueryOption instead
 */
export enum TopGainersAndLosersQueryOption {
  FROM_1_TO_5,
  FROM_5_TO_15,
  FROM_15_TO_50,
  FROM_50_TO_100,
  FROM_100,
  CUSTOM,
}

export enum EpicMoversTabEnum {
  TopGainers,
  TopLosers,
}

export enum HotGappersTabEnum {
  GapUp,
  GapDown,
}

export enum EpicMoversQueryOption {
  FROM_1_TO_5='FROM_1_TO_5',
  FROM_5_TO_15='FROM_5_TO_15',
  FROM_15_TO_50='FROM_15_TO_50',
  FROM_50_TO_100='FROM_50_TO_100',
  FROM_100='FROM_100',
  CUSTOM='',
}

export interface EpicMoversParamType {
  isSymbolUniverseSymbols?: boolean,
  symbolSet?: StocksSymbolSet
  range: EpicMoversQueryOption
}

export interface EpicMoversRequestAndResponse {
  params: EpicMoversParamType,
  responseData: EpicMoversResponse,
}
export enum Week52HighLowStatusType {
  Gainers = 'MORE_THAN_WEEK_52',
  Losers = 'LESS_THAN_WEEK_52',
}

export enum StockExchangeCodeType {
  US_NASDAQ = 'XNYS',
  JAPANESE = 'XJPX',
  EURONEXT = 'EURN',
  FMP_GERMAN = 'XETRA',
  GERMAN = 'XETR',
}

/**
 * @deprecated  please use EpicMoversParamType instead.
 */
export interface TopGainerAndLoserPayload {
  minValue: number;
  maxValue: number;
}

export type ETFParams = OptionalPageAndSizeRequest & {
  filters?: DynamicFilterType;
  projections?: string;
  symbolSet?: string;
}

export type ETF = {
  symbol: string;
  type: string;
  name?: string;
  companyName?: string;
  logoBase64?: string;
  avg30Volume?: number;
}

export type ETFDetailParams = OptionalPageAndSizeRequest & {
  filters?: DynamicFilterType;
  projections?: string;
}

export type ETFDetailResponse = {
  symbol: string;
  avg30Volume: number;
}

export interface SectorDetailParams {
  sectorId: number;
  page: number;
  size: number;
}

export interface SectorDetail {
  sector: string;
  sectorId: number;
  content: BaseSymbolData[];
  nextPageToRead: number;
  totalElements?: number;
}

export interface Sector {
  id?: number;
  sector: string;
  symbolCount: number;
  url?: string;
}

export interface NewStockData extends BaseSymbolData {
  changePercentage: string;
  firstClose: number;
  openingChangePercentage: string;
}

export interface Earning {
  currency: string;
  date: string;
  dateConfirmed: string;
  actualEps: number; // TODO: This property was added to fix TS errors. Check all properties against real data.
  eps: string;
  epsEst: string;
  epsPrior: string;
  epsSurprise: string;
  epsSurprisePercent: string;
  epsType: string;
  exchange: string;
  id: string;
  importance: number;
  name: string;
  notes: string;
  period: string;
  periodYear: number;
  revenue: string;
  revenueEst: string;
  revenuePrior: string;
  revenueSurprise: string;
  revenueSurprisePercent: string;
  revenueType: string;
  symbol: string;
  time: string;
  displayTime?: string;
  displayEpsEst?: string;
  updated: number;
}

export interface IPO {
  created: string;
  currentPrice: number;
  date: string;
  eventId: string;
  filedDate: string;
  firstDayClose: number;
  id: string;
  industry: string;
  ipoStatus: string;
  iposCoopCompanyName: string;
  iposCoopId: number;
  key: string;
  managers: string;
  modified: string;
  offerPrice: number;
  offeringDate: string;
  priceRangeHigh: number;
  priceRangeLow: number;
  rating: number;
  return: number;
  shares: number;
  source: string;
  subKey: string;
  symbol: string;
  updated: string;
  volume: number;
}

export interface Split {
  countryCode: string;
  created: string;
  date: string;
  declaredDate: string;
  description: string;
  exDate: string;
  figi: string;
  flag: string;
  fromFactor: number;
  id: string;
  key: string;
  lastUpdated: string;
  notes: string;
  oldParValue: number;
  oldParValueCurrency: string;
  parValue: number;
  parValueCurrency: string;
  paymentDate: string;
  ratio: number;
  recordDate: string;
  refId: string;
  securityType: string;
  source: string;
  splitType: string;
  subKey: string;
  symbol: string;
  toFactor: number;
  updated: string;
}

export type CompanyLogoAndNameResponse = {
  name: string;
  md5: string;
  symbol: string;
  url: string;
};

export type CompanyLogoAndNameState = {
  logo: string;
  name: string;
  url: string;
};

export type StocksSymbolSet = 'NONE' | 'RETAIL';

/**
 * @deprecated
 * Please use DynamicFilter(services/filter/dynamic-filter.ts) for the common filtering solution
 */
export type Filters = Record<string, TypedObject<string>>

export enum StatementPeriodType {
  ANNUAL = 'ANNUAL',
  QUARTERLY = 'QUARTERLY',
}

export enum RatiosPeriodType {
  ANNUAL = 'ANNUAL',
  QUARTERLY = 'QUARTERLY',
  TTM = 'TTM',
}

export interface BaseStatementSearchParamType {
  symbol: string;
  periodType: StatementPeriodType;
}

export interface BaseDateRangeParamType {
  startDate: string;
  endDate?: NullableString;
}

export interface StatementSearchParamType extends BaseStatementSearchParamType, BaseDateRangeParamType {
}

export interface GapperParamType extends OptionalPageAndSizeRequest, BaseSymbolSetParamType {
  abovePreviousClose: boolean;
}

export interface GapperResponse {
  symbol: string;
  changePercent: number;
  abovePreviousClose: boolean;
  symbolType: string;
  name: string;
  url: string;
}

export interface GapperCountResponse {
  data: number;
}

export interface BaseSymbolSetParamType {
  symbolSet?: StocksSymbolSet;
}

export interface Week52HighLowParamType
  extends OptionalPageAndSizeRequest, OptionalSortingRequest, BaseSymbolSetParamType {
  changeStatus?: Week52HighLowStatusType;
}

export interface Week52HighLowTopListParamType extends BaseSymbolSetParamType {
  topN: number;
}

export interface BaseRatiosParamType {
  symbol: string;
  periodType?: RatiosPeriodType;
}

export interface RatiosByTypeParamType extends BaseDateRangeParamType, BaseRatiosParamType {}

export interface Week52HighLowResponse extends BaseSymbolDataWithName {
  week52High: number;
  week52Low: number;
  lastPrice: number;
  changePercent: number;
  changeStatus: Week52HighLowStatusType;
  symbolType: string;
}

interface SymbolAndSector {
  symbol: string;
  sector: string;
}

export interface OptionalYearAndPeriod {
  calendarYear?: number;
  period?: string;
}

export interface ProfitabilityRatiosResponseElement extends OptionalYearAndPeriod {
  grossProfitMargin: NullableNumber;
  operatingProfitMargin: NullableNumber;
  netProfitMargin: NullableNumber;
  returnOnAssets: NullableNumber;
  returnOnEquity: NullableNumber;
  dividendPayoutRatio?: NullableNumber;
  dividendYield: NullableNumber;
  netIncomePerShare: NullableNumber;
  freeCashFlowPerShare: NullableNumber;
  bookValuePerShare: NullableNumber;
  roic: NullableNumber;
  eps: NullableNumber;
}

export interface LiquidityAndSolvencyRatiosResponseElement extends OptionalYearAndPeriod {
  currentRatio: NullableNumber;
  quickRatio: NullableNumber;
  cashRatio: NullableNumber;
  daysOfSalesOutstanding: NullableNumber;
  debtEquityRatio: NullableNumber;
  interestCoverage: NullableNumber;
  cashFlowToDebtRatio: NullableNumber;
}

export interface ValuationRatiosResponseElement extends OptionalYearAndPeriod{
  priceToBookRatio: NullableNumber;
  priceToSalesRatio: NullableNumber;
  priceEarningsRatio: NullableNumber;
  priceToFreeCashFlowsRatio: NullableNumber;
  priceEarningsToGrowthRatio: NullableNumber;
  enterpriseValueOverEbitda: NullableNumber;
  evToFreeCashFlow: NullableNumber;
}

export interface ProfitabilityRatiosResponse extends SymbolAndSector {
  ratios: ProfitabilityRatiosResponseElement[] | null;
  sectorRatios: ProfitabilityRatiosResponseElement[] | null;
  ttm: ProfitabilityRatiosResponseElement;
  sectorTtm: ProfitabilityRatiosResponseElement;
}

export interface LiquidityAndSolvencyRatiosResponse extends SymbolAndSector {
  ratios: LiquidityAndSolvencyRatiosResponseElement[] | null;
  sectorRatios: LiquidityAndSolvencyRatiosResponseElement[] | null;
  ttm: LiquidityAndSolvencyRatiosResponseElement;
  sectorTtm: LiquidityAndSolvencyRatiosResponseElement;
}

export interface ValuationRatiosResponse extends SymbolAndSector {
  ratios: ValuationRatiosResponseElement[] | null;
  sectorRatios: ValuationRatiosResponseElement[] | null;
  ttm: ValuationRatiosResponseElement;
  sectorTtm: ValuationRatiosResponseElement;
}

export interface Week52HighLowTopListResponse {
  topNHigh: Week52HighLowResponse[];
  topNLow: Week52HighLowResponse[];
}

export interface RatiosResponse {
  profitability: ProfitabilityRatiosResponse;
  liquidityAndSolvency: LiquidityAndSolvencyRatiosResponse;
  valuation: ValuationRatiosResponse;
}

interface CommonStatementType {
  date: string;
  currency: string;
  cik: string;
  fillingDate: string;
  acceptedDate: number;
  link: string;
  finalLink: string;
  symbol: string;
  calendarYear: number;
  period: string;
}

export interface Income extends CommonStatementType {
  revenue: number;
  costOfRevenue: number;
  grossProfit: number;
  grossProfitRatio: number;
  researchAndDevelopmentExpenses: number;
  generalAndAdministrativeExpenses: number;
  sellingAndMarketingExpenses: number;
  sellingGeneralAndAdministrativeExpenses: number;
  otherExpenses: number;
  operatingExpenses: number;
  costAndExpenses: number;
  interestIncome: number;
  interestExpense: number;
  depreciationAndAmortization: number;
  ebitda: number;
  ebitdaRatio: number;
  operatingIncome: number;
  operatingIncomeRatio: number;
  totalOtherIncomeExpensesNet: number;
  incomeBeforeTax: number;
  incomeBeforeTaxRatio: number;
  incomeTaxExpense: number;
  netIncome: number;
  netIncomeRatio: number;
  eps: number;
  epsDiluted: number;
  weightedAverageShsOut: number;
  weightedAverageShsOutDil: number;
}

export interface BalanceSheet extends CommonStatementType {
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  cashAndShortTermInvestments: number;
  netReceivables: number;
  inventory: number;
  otherCurrentAssets: number;
  totalCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  goodwillAndIntangibleAssets: number;
  longTermInvestments: number;
  taxAssets: number;
  otherNonCurrentAssets: number;
  totalNonCurrentAssets: number;
  otherAssets: number;
  totalAssets: number;
  accountPayables: number;
  shortTermDebt: number;
  taxPayables: number;
  deferredRevenue: number;
  otherCurrentLiabilities: number;
  totalCurrentLiabilities: number;
  longTermDebt: number;
  deferredRevenueNonCurrent: number;
  deferredTaxLiabilitiesNonCurrent: number;
  otherNonCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  otherLiabilities: number;
  capitalLeaseObligations: number;
  totalLiabilities: number;
  preferredStock: number;
  commonStock: number;
  retainedEarnings: number;
  accumulatedOtherComprehensiveIncomeLoss: number;
  otherTotalStockholdersEquity: number;
  totalStockholdersEquity: number;
  totalEquity: number;
  totalLiabilitiesAndStockholdersEquity: number;
  minorityInterest: number;
  totalLiabilitiesAndTotalEquity: number;
  totalInvestments: number;
  totalDebt: number;
  netDebt: number;
}

export interface CashFlow extends CommonStatementType {
  netIncome: number;
  depreciationAndAmortization: number;
  deferredIncomeTax: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;
  accountsReceivables: number;
  inventory: number;
  accountsPayables: number;
  otherWorkingCapital: number;
  otherNonCashItems: number;
  netCashProvidedByOperatingActivities: number;
  investmentsInPropertyPlantAndEquipment: number;
  acquisitionsNet: number;
  purchasesOfInvestments: number;
  salesMaturitiesOfInvestments: number;
  otherInvestingActivities: number;
  netCashUsedForInvestingActivities: number;
  debtRepayment: number;
  commonStockIssued: number;
  commonStockRepurchased: number;
  dividendsPaid: number;
  otherFinancingActivities: number;
  netCashUsedProvidedByFinancingActivities: number;
  effectOfForexChangesOnCash: number;
  netChangeInCash: number;
  cashAtEndOfPeriod: number;
  cashAtBeginningOfPeriod: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  freeCashFlow: number;
}

export interface StatementApiResponse {
  income: Income[];
  balanceSheet: BalanceSheet[];
  cashFlow: CashFlow[];
}

export interface SymbolStatementApiResponse extends StatementApiResponse {
  symbol: string;
}
